import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";

import { useApi } from "../hooks/useApi";

const EditServiceDialog = ({
  showEditDialog,
  setShowEditDialog,
  selectedService,
  showServiceAlert,
  setServiceShowAlert,
  type,
  selectedBarber,
  onSave,
}) => {
  const api = useApi();

  const [service, setService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  useEffect(() => {
    if (selectedService) {
      setService(selectedService);
    }
  }, [selectedService]);

  const handleClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setShowEditDialog({ show: false, service: service });
    }
  };

  const saveService = async () => {
    setIsSubmitting(true);
    setShowAlert({ show: false, message: "", type: "" });

    try {
      const response =
        type === "tenants"
          ? await api(`/tenants/services.php`, {
              method: "PUT",
              body: JSON.stringify(service),
            })
          : await api(`/barbers/services.php?barber_id=${selectedBarber}`, {
              method: "PUT",
              body: JSON.stringify({ services: [service] }),
            });

      setServiceShowAlert({
        show: true,
        message: "Successfully updated service.",
        type: "success",
      });
      setShowEditDialog({ show: false, service: response.data.service });
      onSave();
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={showEditDialog} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Edit Service
        <IconButton
          disabled={isSubmitting}
          aria-label="close"
          onClick={() => setShowEditDialog({ show: false, service: service })}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {showAlert.show === true && (
          <Alert sx={{ mb: 2 }} severity={showAlert.type}>
            {showAlert.message}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Name
            </Typography>
            <TextField
              variant="outlined"
              name="serviceName"
              label=""
              fullWidth
              placeholder="Service Name"
              value={service?.name || ""}
              disabled={isSubmitting}
              onChange={(e) => {
                setService((prev) => ({ ...prev, name: e.target.value }));
              }}
            />
          </Grid>
          <Grid size={12}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Description
            </Typography>
            <TextField
              variant="outlined"
              name="serviceDescription"
              label=""
              fullWidth
              placeholder="Service Description"
              value={service?.description || ""}
              disabled={isSubmitting}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  description: e.target.value,
                }));
              }}
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Price
            </Typography>
            <TextField
              variant="outlined"
              name="servicePrice"
              label=""
              fullWidth
              type="number"
              placeholder="Service Price"
              value={service?.price || ""}
              disabled={isSubmitting}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment>$</InputAdornment>,
                },
              }}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  price: e.target.value,
                }));
              }}
            />
          </Grid>
          <Grid size={6}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Service Duration
            </Typography>
            <TextField
              variant="outlined"
              name="serviceDuration"
              label=""
              fullWidth
              type="number"
              placeholder="Service Duration (minutes)"
              value={service?.duration_minutes || ""}
              disabled={isSubmitting}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment>mins</InputAdornment>,
                },
              }}
              onChange={(e) => {
                setService((prev) => ({
                  ...prev,
                  duration_minutes: e.target.value,
                }));
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", ml: 2, mr: 2 }}>
        {/* Left side */}
        <FormControlLabel
          control={
            <Switch
              checked={service?.is_active === 1}
              disabled={isSubmitting}
              onChange={(e) =>
                setService((prev) => ({
                  ...prev,
                  is_active: e.target.checked ? 1 : 0,
                }))
              }
            />
          }
          label="Active"
        />

        {/* Right side */}
        <Box>
          <Button
            onClick={() => setShowEditDialog({ show: false, service: service })}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => saveService()}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <CircularProgress
                enableTrackSlot
                size="25px"
                sx={{ color: "text.primary" }}
              />
            ) : (
              "Save"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditServiceDialog;
